<script setup lang="ts">
import {
  head,
  last,
} from 'lodash-es';
import {
  computed,
  ref,
  watch,
} from 'vue';
import { useRoute } from 'vue-router';

import type {
  ISmartFilterColumn,
  ISmartFilterEmitsDataValues,
} from '@modules/smart-filters/types';
import type {
  ITableFilterGetColumnPayload,
  ITableFilterPresetGetPayload,
  ITableFilterSaveColumnPayload,
} from '@modules/tables/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import { useDevice } from '@modules/core/composables/device';
import { ERouteNamespace } from '@modules/core/services/router/enums';
import { useCurrentNamespace } from '@modules/permissions/composables';
import VSmartFilter from '@modules/smart-filters/components/VSmartFilter/VSmartFilter.vue';
import {
  isSmartFilterCompareColumnIdData,
  isSmartFilterDateColumnIdData,
} from '@modules/smart-filters/utils';
import {
  QA_TABLES,
  TABLES_PERMISSIONS,
  TRANSLATION_GROUP_TABLE,
} from '@modules/tables/constants';
import {
  useAddTableFilterColumnMutation,
  useRemoveTableFilterColumnMutation,
  useTableFilterPresetMutation,
} from '@modules/tables/mutations';
import {
  useTableFilterColumnQuery,
  useTableFilterPresetQuery,
} from '@modules/tables/queries';

import type {
  ITableFilterComponentProps,
  ITableFilterEmits,
} from './types';

const props = defineProps<ITableFilterComponentProps>();
const emit = defineEmits<ITableFilterEmits>();

const route = useRoute();
const { isAdminNamespace } = useCurrentNamespace();
const { isMobile } = useDevice();

const isOpenDialog = ref(false);
const isQueryEnabled = ref(false);
const isFilterApplied = ref(false);
const selectedColumnName = ref<string>();
const selectedFilterId = ref<number>();

const pathByNamespace = computed(() => props.pathForFilter.replace(':namespace', isAdminNamespace.value ? ERouteNamespace.ADMIN : ERouteNamespace.AFFILIATE));

const presetFiltersAll = computed<ITableFilterPresetGetPayload>(() => ({
  filterName: props.filterName,
  path_for_filter: pathByNamespace.value,
  action_for_filter: props.actionForFilter,
  by_merchant: props.byMerchant,
}));

const presetFilters = computed<ITableFilterPresetGetPayload>(() => ({
  ...presetFiltersAll.value,
  filter_set_id: selectedFilterId.value,
}));

const columnFilters = computed<ITableFilterGetColumnPayload>(() => ({
  path_for_filter: pathByNamespace.value,
  action_for_filter: props.actionForFilter,
  by_merchant: props.byMerchant,
  filterId: selectedFilterId.value,
  columnName: selectedColumnName.value,
}));

const {
  data: filterDataAll,
  isFetching: isFetchingPresetAll,
  refetch: refetchPresets,
  setData: setDataAllFilters,
} = useTableFilterPresetQuery(presetFilters, false);

const {
  data: selectedColumnData,
  isFetching: isFetchingColumnQuery,
  setData: setColumnData,
} = useTableFilterColumnQuery(columnFilters, isQueryEnabled);

const isLoadingColumn = computed(() => isFetchingColumnQuery.value && !!selectedColumnName.value);

function getColumnByName(columnName?: string): ISmartFilterColumn | undefined {
  return columnName && props.columnOptions[columnName]
    ? {
        ...props.columnOptions[columnName],
        id: columnName,
      }
    : undefined;
}

const { mutateAsync: saveNewPreset, isLoading: isLoadingNewPreset } = useTableFilterPresetMutation();
const { mutateAsync: addColumnData, isLoading: isSaveLoading } = useAddTableFilterColumnMutation();
const { mutateAsync: removeColumnData, isLoading: isRemoveLoading } = useRemoveTableFilterColumnMutation();

const isButtonLoading = computed(() => isFetchingPresetAll.value || isLoadingNewPreset.value);

const columns = computed<ISmartFilterColumn[]>(() => {
  if (!filterDataAll.value?.allowed_filter_segments) {
    return [];
  }

  const result: ISmartFilterColumn[] = [];

  for (const filterCol of filterDataAll.value.allowed_filter_segments) {
    const item = props.columnOptions[filterCol];

    if (item) {
      result.push({
        ...item,
        id: filterCol,
        isEmpty: !filterDataAll.value?.upload_filter_segments.includes(filterCol),
      });
    }
  }

  return result;
});

watch(
  () => props.filterSetId,
  () => {
    selectedFilterId.value = props.filterSetId;
    isFilterApplied.value = !!selectedFilterId.value;
  },
  { immediate: true },
);

function openDialog(): void {
  isOpenDialog.value = true;
}

function closeDialog(): void {
  isOpenDialog.value = false;
  isQueryEnabled.value = false;
}

function onHide(): void {
  closeDialog();
}

function applyFilter(): void {
  isFilterApplied.value = true;
  emit('apply-filter', selectedFilterId.value);
  closeDialog();
}

function clearFilter(): void {
  isFilterApplied.value = false;
  emit('clear-filter');
  closeDialog();
}

function setFilterId(id?: number): void {
  selectedFilterId.value = id;
}

function emptyFilter(columnName: string, isEmpty?: boolean): void {
  setDataAllFilters((queryData) => {
    if (filterDataAll.value) {
      return {
        ...filterDataAll.value,
        upload_filter_segments:
          isEmpty
            ? filterDataAll.value?.upload_filter_segments.filter((id) => id !== columnName)
            : [...filterDataAll.value?.upload_filter_segments ?? [], columnName],
      };
    }
    return queryData;
  });
}

async function saveColumnData({
  columnName,
  data,
  needToAdd,
  isEmpty,
}: ISmartFilterEmitsDataValues): Promise<void> {
  emptyFilter(columnName, isEmpty);

  const payload: ITableFilterSaveColumnPayload = {
    ...columnFilters.value,
    columnName,
    filter_segment: {
      data,
    },
  };

  if (needToAdd) {
    if (columnName === columnFilters.value.columnName) {
      setColumnData((queryData) => {
        if (
          isSmartFilterCompareColumnIdData(getColumnByName(payload.columnName), data)
          || isSmartFilterDateColumnIdData(getColumnByName(payload.columnName), data)
        ) {
          return {
            [columnName]: data,
          };
        }

        return queryData;
      });
    }

    await addColumnData(payload);
  } else {
    await removeColumnData(payload);
  }
}

function handleSelectionColumn(columnIdentifier: string): void {
  if (!columnIdentifier) {
    return;
  }

  selectedColumnName.value = columnIdentifier;
}

async function showFilter(): Promise<void> {
  const { data } = await refetchPresets();

  if (!selectedFilterId.value) {
    setFilterId(head(data?.filter_set_ids ?? []));
  }
  isQueryEnabled.value = true;

  openDialog();
}

function selectPreset(id: number): void {
  setFilterId(id);
  void refetchPresets();
  if (selectedColumnName.value) {
    handleSelectionColumn(selectedColumnName.value);
  }
}

async function addNewPreset(): Promise<void> {
  const data = await saveNewPreset({
    ...presetFiltersAll.value,
    filter_set_id: 'new',
  });
  setFilterId(last(data.filter_set_ids ?? []));
  setDataAllFilters((queryData) => {
    return {
      ...queryData,
      ...data,
    };
  });

  if (selectedColumnName.value) {
    handleSelectionColumn(selectedColumnName.value);
  }
}

watch(() => route.path, () => {
  isQueryEnabled.value = false;
  selectedFilterId.value = undefined;
});
</script>

<template>
  <VIconButton v-data-qa-auto-id="'VTableFilter'"
    v-if="$hasPermission(TABLES_PERMISSIONS.FILTERSETS_SHOW)"
    v-data-test="{ el: QA_TABLES.filterButton }"
    :icon="`pi-${selectedFilterId !== 0 && isFilterApplied ? 'filter-fill' : 'filter'}`"
    :size="isMobile ? undefined : 'large'"
    :loading="isButtonLoading"
    :disabled="disabled"
    :class="{ 'text-muted-color': isMobile && !isFilterApplied }"
    @click="showFilter"
  />
  <VDialog
    :visible="isOpenDialog"
    :title="$getTranslation(TRANSLATION_GROUP_TABLE, 'FILTER_TITLE')"
    size="large"
    @hide="onHide"
  >
    <div>
      <div class="mb-3 flex flex-wrap gap-2">
        <VButton
          v-for="id in filterDataAll?.filter_set_ids"
          :key="id"
          v-data-test="{ el: QA_TABLES.filterPresetButton, presetId: id }"
          :label="$getTranslation(TRANSLATION_GROUP_TABLE, 'NO_NAME', { id })"
          text
          :plain="selectedFilterId !== id"
          :outlined="selectedFilterId === id"
          @click="selectPreset(id)"
        />
        <VButton
          v-data-test="{ el: QA_TABLES.filterPresetAddButton }"
          :label="$getTranslation(TRANSLATION_GROUP_TABLE, 'ADD_NEW_FILTER')"
          icon="pi pi-plus"
          text
          @click="addNewPreset"
        />
      </div>
      <div>
        <VSmartFilter
          :data="selectedColumnData"
          :selected-column-id="selectedColumnName"
          :columns="columns"
          :translation-path="props.translationPath"
          :is-loading-column="isLoadingColumn"
          :is-saving-column="isSaveLoading || isRemoveLoading"
          sort-checkboxes-after-change-item
          @data-change="saveColumnData"
          @column-change="handleSelectionColumn"
        />
      </div>
    </div>
    <template #footer>
      <VButton
        v-data-test="{ el: QA_TABLES.filterClear }"
        :label="$getTranslation(TRANSLATION_GROUP_TABLE, 'CANCEL_FILTER')"
        icon="pi pi-times"
        outlined
        @click="clearFilter"
      />
      <VButton
        v-data-test="{ el: QA_TABLES.filterApply }"
        :label="$getTranslation(TRANSLATION_GROUP_TABLE, 'APPLY_FILTER')"
        icon="pi pi-check"
        autofocus
        @click="applyFilter"
      />
    </template>
  </VDialog>
</template>
